/**
 * builderLogic - the report builder's pure core (docs/plans/crm-report-builder-search-collab-plan.md
 * A4.2 to A4.4, B4.3, B4.5 and B4.6): the spec state and its valid next choices (a choice that would
 * break a rule is DISABLED with the reason, never silently dropped), the catalog words, the "how this
 * counts" line, the result pivot into table rows and bar widths (BarRow's rule), the time axis labels,
 * the CSV file name, the share rules for display, the pin helpers, and the tolerant readers api.ts
 * passes every server answer through.
 *
 * React-free and axios-free. Never import ../lib or ./api here: both reach the api client, and this
 * module's own functions must be testable with no mocks (the recruitLogic / outreachLogic precedent).
 * Where a list restates one in lib.ts (the lifecycle and eligibility words, the Reports page's date
 * presets as rangeWindow computes them), builderLogic.test.ts holds the two to agreement.
 *
 * The server is the authority: it checks every spec against its catalog (crmReportBuilder/spec.js)
 * and answers 400 with a plain sentence. The rules here restate B4.3 so the panel can disable a choice
 * BEFORE the round trip; when the two ever disagree, the server's sentence is what the person sees.
 */
import type {
  RangePreset,
  ReportAxes,
  ReportCatalog,
  ReportChart,
  ReportFamily,
  ReportFilterDef,
  ReportFilterKey,
  ReportFilters,
  ReportGrouping,
  ReportMeasure,
  ReportNotes,
  ReportOption,
  ReportRange,
  ReportRangePreset,
  ReportResult,
  ReportResultRow,
  ReportSpec,
  ReportSubject,
  ReportTotals,
  ReportUnit,
  ReportVisibility,
  SavedReport,
  SavedReportSummary,
  TimeGrain,
} from './types';

// ---------------------------------------------------------------------------------------------
// The rules' numbers (plan A4.4, B4.3, B4.4)
// ---------------------------------------------------------------------------------------------

export const SPEC_VERSION = 1 as const;
/** Two ways at most: time, the first grouping, the second grouping and a family measure each count one. */
export const MAX_AXES = 2;
/** A grouping shows its top 50 alone, its top 15 when paired; the rest is one "Other (N more)" row. */
export const TOP_SINGLE = 50;
export const TOP_PAIRED = 15;
/** The most buckets one time split may show (62 days, 53 weeks, 36 months). */
export const GRAIN_MAX: Readonly<Record<TimeGrain, number>> = { day: 62, week: 53, month: 36 };
/** A custom range may cover three years at most. */
export const MAX_RANGE_YEARS = 3;
/** Custom dates outside these years are refused (crmReportBuilder/spec.js MIN_YEAR and MAX_YEAR). */
const MIN_YEAR = 2000;
const MAX_YEAR = 2099;
/** A lead-source filter is text of at most 200 characters (spec.js MAX_LEAD_SOURCE_LENGTH). */
const MAX_LEAD_SOURCE_LENGTH = 200;
/** crm.saved_reports CHECKs: name 1 to 80 characters, description at most 500. */
export const REPORT_NAME_MAX = 80;
export const REPORT_DESCRIPTION_MAX = 500;
/** The key of the remainder row past the top N (plan B4.3). */
export const OTHER_KEY = '__other__';
/**
 * The keys a missing value comes back under, as the mirrored tab functions key it (plan B4.3; the
 * rb_ functions, plan B13 R-1): '(none)' for every grouping except criterion, '(no criterion)', and
 * landing_variant, '(default)' (the control form, as the Conversion tab keys it). The first is the
 * key the pivot gives a row that comes back with no key at all.
 */
export const NONE_KEYS: readonly string[] = ['(none)', '(no criterion)', '(default)'];
/** The one key the pivot gives the single value column or row. */
export const MEASURE_KEY = '__value__';
export const FILTER_KEYS: readonly ReportFilterKey[] = ['study_id', 'owner', 'lead_source', 'eligibility'];
export const DEFAULT_SUBJECT = 'leads';
export const DEFAULT_PRESET: RangePreset = 'last_30';

/**
 * Pinned reports, per device (plan B4.6, decision R6 a): a JSON array of report ids under
 * `crm.reportBuilder.pinned`. The component reads it with lib's getPref(PINNED_PREF_KEY, '') and
 * writes it with setPref(PINNED_PREF_KEY, serializePinned(ids)); lib adds the `crm.` prefix.
 */
export const PINNED_PREF_KEY = 'reportBuilder.pinned';
export const PINNED_STORAGE_KEY = `crm.${PINNED_PREF_KEY}`;
export const MAX_PINNED = 50;

// ---------------------------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------------------------

/** The date choices: the Reports page's six presets (same labels) plus custom dates. */
export const RANGE_CHOICES: readonly { key: ReportRangePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7', label: 'Last 7 days' },
  { key: 'last_30', label: 'Last 30 days' },
  { key: 'last_90', label: 'Last 90 days' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom dates' },
];

export const GRAIN_CHOICES: readonly { key: TimeGrain; label: string; unit: string; units: string }[] = [
  { key: 'day', label: 'Day', unit: 'day', units: 'days' },
  { key: 'week', label: 'Week', unit: 'week', units: 'weeks' },
  { key: 'month', label: 'Month', unit: 'month', units: 'months' },
];

export const CHART_CHOICES: readonly { key: ReportChart; label: string }[] = [
  { key: 'bars', label: 'Bars' },
  { key: 'table', label: 'Table' },
];

/** The lead pipeline in funnel order with lib LIFECYCLES' labels (the test holds them equal). */
export const STATUS_WORDS: readonly ReportOption[] = [
  { key: 'new', label: 'New' },
  { key: 'contacting', label: 'Contacting' },
  { key: 'qualified_pending_records', label: 'Qualified - Pending Records' },
  { key: 'ready_to_schedule', label: 'Ready to Schedule' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'screened', label: 'Screened' },
  { key: 'randomized', label: 'Randomized' },
  { key: 'screen_failed', label: 'Screen Failed' },
  { key: 'not_eligible', label: 'Not Eligible' },
  { key: 'not_interested', label: 'Not Interested' },
  { key: 'unreachable', label: 'Unreachable' },
];

/** Eligibility on arrival, with lib INCOMING_ELIGIBILITY_OPTIONS' labels (the test holds them equal). */
export const ELIGIBILITY_WORDS: readonly ReportOption[] = [
  { key: 'eligible', label: 'Eligible' },
  { key: 'needs_review', label: 'Needs review' },
  { key: 'pending', label: 'Incomplete screener' },
  { key: 'not_eligible', label: 'Not eligible' },
];

const FILTER_WORDS: Readonly<Record<ReportFilterKey, string>> = {
  study_id: 'Study',
  owner: 'Recruiter',
  lead_source: 'Lead source',
  eligibility: 'Eligibility on arrival',
};

const EXIT_KEYS = ['screen_failed', 'not_eligible', 'not_interested', 'unreachable'];

// ---------------------------------------------------------------------------------------------
// The v1 catalog, restated from plan B4.3 (decision R2 a)
// ---------------------------------------------------------------------------------------------

const measureDef = (
  key: string,
  label: string,
  additive: boolean,
  extra: Partial<Pick<ReportMeasure, 'family' | 'unit'>> = {},
): ReportMeasure => ({ key, label, additive, family: null, admin: false, unit: 'count', ...extra });

const groupingDef = (key: string, label: string, rule: string | null = null): ReportGrouping => ({ key, label, rule });

const filterDef = (key: ReportFilterKey, label: string, options: readonly ReportOption[] | null = null): ReportFilterDef => ({
  key,
  label,
  options: options ? options.map((o) => ({ ...o })) : null,
});

const words = (keys: readonly string[]): ReportOption[] =>
  keys.map((key) => ({ key, label: STATUS_WORDS.find((w) => w.key === key)?.label ?? humanize(key) }));

/**
 * The v1 catalog as plan B4.3 writes it. The SERVER's catalog (GET /report-builder/catalog) is the one
 * the builder offers; this copy is the fallback when a function is called without one (emptySpec,
 * applyChoice) and the tests' fixture. Its words are the plan's; the server's words win at run time.
 */
export const V1_CATALOG: ReportCatalog = {
  spec_version: SPEC_VERSION,
  subjects: [
    {
      key: 'leads',
      label: 'Leads',
      mirrors: 'Funnel',
      time_label: 'when the lead came in',
      counts_as: "Study and recruiter = each lead's current one; dates = when the lead came in",
      measures: [
        measureDef('new_leads', 'New leads', true),
        measureDef('reached', 'Reached each stage', false, {
          family: { key: 'reached', label: 'Stage', members: words(STATUS_WORDS.slice(0, 7).map((w) => w.key)) },
        }),
        measureDef('here_now', 'Here now, by stage', true, {
          family: { key: 'here_now', label: 'Stage', members: words(STATUS_WORDS.map((w) => w.key)) },
        }),
        measureDef('closed_as', 'Closed as', false, {
          family: { key: 'closed_as', label: 'Exit status', members: words(EXIT_KEYS) },
        }),
      ],
      groupings: [
        groupingDef('study', 'Study', 'current'),
        groupingDef('lead_source', 'Lead source', 'first touch'),
        groupingDef('recruiter', 'Recruiter', 'current owner'),
        groupingDef('eligibility', 'Eligibility on arrival', 'on arrival'),
        groupingDef('landing_variant', 'Landing variant', 'first touch'),
      ],
      filters: [
        filterDef('study_id', 'Study'),
        filterDef('owner', 'Recruiter'),
        filterDef('lead_source', 'Lead source'),
        filterDef('eligibility', 'Eligibility on arrival', ELIGIBILITY_WORDS),
      ],
    },
    {
      key: 'status_changes',
      label: 'Status changes',
      mirrors: 'Dispositions',
      time_label: 'when the status changed',
      counts_as: 'Recruiter and study = at the time of the change',
      measures: [
        measureDef('leads', 'Leads moved', false),
        measureDef('moves', 'Every move', true),
      ],
      groupings: [
        groupingDef('to_status', 'Status'),
        groupingDef('recruiter', 'Recruiter', 'at the time'),
        groupingDef('study', 'Study', 'at the time'),
        groupingDef('lead_source', 'Lead source', 'first touch'),
      ],
      filters: [filterDef('study_id', 'Study'), filterDef('owner', 'Recruiter')],
    },
    {
      key: 'disqualifications',
      label: 'Prescreen disqualifications',
      mirrors: 'Screening (Prescreen)',
      time_label: 'when the disqualification was recorded',
      counts_as: 'Prescreen disqualifications recorded in the window',
      measures: [
        measureDef('count', 'Disqualifications', true),
        measureDef('leads', 'Leads disqualified', false),
      ],
      groupings: [
        groupingDef('category', 'Reason category'),
        groupingDef('criterion', 'Criterion'),
        groupingDef('study', 'Study', 'recorded'),
        groupingDef('lead_source', 'Lead source', 'recorded'),
        groupingDef('recruiter', 'Recorded by', 'who recorded it'),
      ],
      filters: [filterDef('study_id', 'Study'), filterDef('owner', 'Recorded by'), filterDef('lead_source', 'Lead source')],
    },
    {
      key: 'activity',
      label: 'Team activity',
      mirrors: 'Productivity',
      time_label: "each activity's own time",
      counts_as:
        "Calls, texts and emails by the teammate who made them (automated sends are not counted, as on Productivity); study = the lead's current one",
      measures: [
        measureDef('calls_out', 'Calls out', true),
        measureDef('calls_connected', 'Calls connected', true),
        measureDef('calls_in_handled', 'Inbound calls handled', true),
        measureDef('talk_time_sec', 'Talk time', true, { unit: 'seconds' }),
        measureDef('texts', 'Texts', true),
        measureDef('emails', 'Emails', true),
        measureDef('leads_worked', 'Leads worked', false),
      ],
      groupings: [groupingDef('teammate', 'Teammate', 'who made it'), groupingDef('study', 'Study', 'current')],
      filters: [filterDef('study_id', 'Study'), filterDef('owner', 'Teammate')],
    },
  ],
};

/** A subject's starting point: the walkthrough's (A4.3 step 1) for Leads, the mirrored tab's for the rest. */
const SUBJECT_DEFAULTS: Readonly<Record<string, { measure: string; rows: string | null }>> = {
  leads: { measure: 'reached', rows: 'study' },
  status_changes: { measure: 'leads', rows: 'to_status' },
  disqualifications: { measure: 'count', rows: 'category' },
  activity: { measure: 'calls_out', rows: 'teammate' },
};

// ---------------------------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------------------------

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);

/** A record's OWN entry (so a key like "constructor" never reaches Object.prototype). */
const own = <T>(record: Readonly<Record<string, T>>, key: string): T | undefined =>
  Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;

/** A non-blank string, else null. */
const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/** A finite number (numeric strings included, as PostgREST may send numeric), else null. */
const numberOf = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** A key as a string (a number key is stringified), or null. */
const keyOf = (v: unknown): string | null => {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** 'lead_source' -> 'Lead source'. */
export function humanize(key: string): string {
  const s = key.replace(/[_-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : key;
}

/** True when a string holds a control character (below U+0020, or U+007F), as spec.js checks it. */
const hasControlCharacter = (s: string): boolean => {
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 32 || c === 127) return true;
  }
  return false;
};

/** Ends a sentence with a full stop when it has no closing punctuation. */
const asSentence = (s: string): string => {
  const t = s.trim();
  return !t || /[.!?]$/.test(t) ? t : `${t}.`;
};

export const isOtherKey = (key: string | null | undefined): boolean => key === OTHER_KEY;
export const isNoneKey = (key: string | null | undefined): boolean =>
  key === null || key === undefined || key === '' || NONE_KEYS.includes(key);

const emptyFilters = (): ReportFilters => ({ study_id: null, owner: null, lead_source: null, eligibility: null });

// ---------------------------------------------------------------------------------------------
// Catalog lookups
// ---------------------------------------------------------------------------------------------

/** The catalog to use: the one given, else the v1 fallback. */
export const resolveCatalog = (catalog?: ReportCatalog | null): ReportCatalog => catalog ?? V1_CATALOG;

export function subjectOf(subject: string, catalog?: ReportCatalog | null): ReportSubject | null {
  return resolveCatalog(catalog).subjects.find((s) => s.key === subject) ?? null;
}

export function measureOf(subject: ReportSubject | null, key: string): ReportMeasure | null {
  return subject?.measures.find((m) => m.key === key) ?? null;
}

export function groupingOf(subject: ReportSubject | null, key: string | null): ReportGrouping | null {
  if (!key) return null;
  return subject?.groupings.find((g) => g.key === key) ?? null;
}

/** The spec's measure's family (its own axis), or null. */
export function familyOf(spec: ReportSpec, catalog?: ReportCatalog | null): ReportFamily | null {
  return measureOf(subjectOf(spec.subject, catalog), spec.measure)?.family ?? null;
}

/** How many ways the spec splits: time, the two groupings and a family measure each count one. */
export function axisCount(spec: ReportSpec, catalog?: ReportCatalog | null): number {
  return (spec.rows ? 1 : 0) + (spec.columns ? 1 : 0) + (spec.time ? 1 : 0) + (familyOf(spec, catalog) ? 1 : 0);
}

/** The top N the server keeps per grouping: 50 alone, 15 when the spec splits two ways. */
export function topN(spec: ReportSpec, catalog?: ReportCatalog | null): { a: number; b: number } {
  return { a: axisCount(spec, catalog) >= 2 ? TOP_PAIRED : TOP_SINGLE, b: TOP_PAIRED };
}

/**
 * The values' unit. Talk time comes back in SECONDS (the rb_ function returns what
 * crm.productivity_stats returns, the parity proof's rule) whatever word the catalog uses for its
 * display, so a `_sec` key is seconds; the UI shows it as minutes (formatValue).
 */
export function unitOf(measure: ReportMeasure | null, measureKey: string): ReportUnit {
  if (/_sec$/.test(measureKey) || measure?.unit === 'seconds') return 'seconds';
  return 'count';
}

// ---------------------------------------------------------------------------------------------
// Calendar days. A day is a number (days since 1970-01-01 of the CALENDAR date), so no time zone or
// daylight-saving shift can move it. The viewer's local calendar is the time zone the run is bucketed
// in: api.ts sends the browser's IANA zone with every run.
// ---------------------------------------------------------------------------------------------

const DAY_MS = 86400000;

/** The local calendar day of an instant. */
export function dayNumberOf(d: Date): number {
  return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS);
}

/** 'YYYY-MM-DD' (a real date) to a day number, else null. */
export function parseDay(s: unknown): number | null {
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const t = Date.UTC(y, mo - 1, d);
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) return null;
  return Math.round(t / DAY_MS);
}

/** A day number to 'YYYY-MM-DD'. */
export function dayString(n: number): string {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

/** The calendar year of a day number. */
const yearOf = (n: number): number => new Date(n * DAY_MS).getUTCFullYear();

/** 0 = Monday ... 6 = Sunday (1970-01-01 was a Thursday). */
const weekdayIndex = (n: number): number => (((n + 3) % 7) + 7) % 7;
const mondayOf = (n: number): number => n - weekdayIndex(n);
const monthIndexOf = (n: number): number => {
  const d = new Date(n * DAY_MS);
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
};
const monthStartOf = (n: number): number => {
  const d = new Date(n * DAY_MS);
  return Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / DAY_MS);
};
const nextMonthStart = (n: number): number => {
  const d = new Date(n * DAY_MS);
  return Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1) / DAY_MS);
};

/** The first day of the bucket holding day n (Postgres date_trunc: weeks start on Monday). */
export function bucketStart(n: number, grain: TimeGrain): number {
  if (grain === 'week') return mondayOf(n);
  if (grain === 'month') return monthStartOf(n);
  return n;
}

const nextBucket = (n: number, grain: TimeGrain): number => {
  if (grain === 'week') return n + 7;
  if (grain === 'month') return nextMonthStart(n);
  return n + 1;
};

/** How many buckets of `grain` the days first..last (inclusive) touch. */
export function bucketCount(first: number, last: number, grain: TimeGrain): number {
  if (last < first) return 0;
  if (grain === 'week') return (mondayOf(last) - mondayOf(first)) / 7 + 1;
  if (grain === 'month') return monthIndexOf(last) - monthIndexOf(first) + 1;
  return last - first + 1;
}

const PRESET_DAYS: Readonly<Record<string, number>> = { last_7: 7, last_30: 30, last_90: 90 };

/**
 * A preset as an explicit [from, to) window, exactly as lib.ts rangeWindow turns the Reports page's
 * tokens into dates (last_7 = '7' and so on): `to` null means now. The server resolves presets itself
 * (plan B4.3); the builder uses this for its span rules and the time axis.
 */
export function presetRange(preset: string, now: Date = new Date()): { from: string | null; to: string | null } {
  if (preset === 'all') return { from: null, to: null };
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'today') return { from: startOfToday.toISOString(), to: null };
  if (preset === 'yesterday') {
    const startYesterday = new Date(startOfToday.getTime() - 86400e3);
    return { from: startYesterday.toISOString(), to: startOfToday.toISOString() };
  }
  const days = own(PRESET_DAYS, preset);
  if (days) return { from: new Date(now.getTime() - days * 86400e3).toISOString(), to: null };
  return { from: null, to: null };
}

/** The calendar days a range covers (inclusive), or null for all time or unreadable custom dates. */
export function rangeDays(range: ReportRange, now: Date = new Date()): { first: number; last: number } | null {
  if (range.preset === 'custom') {
    const first = parseDay(range.from);
    const last = parseDay(range.to);
    return first === null || last === null ? null : { first, last };
  }
  const { from, to } = presetRange(range.preset, now);
  if (!from) return null;
  const start = new Date(from);
  const endExclusive = to ? new Date(to) : now;
  const first = dayNumberOf(start);
  return { first, last: Math.max(first, dayNumberOf(new Date(endExclusive.getTime() - 1))) };
}

/** True when a custom range covers more than three years. */
const overMaxYears = (first: number, last: number): boolean => {
  const d = new Date(first * DAY_MS);
  const limit = Math.round(Date.UTC(d.getUTCFullYear() + MAX_RANGE_YEARS, d.getUTCMonth(), d.getUTCDate()) / DAY_MS);
  return last >= limit;
};

/** Custom dates to start from when a person picks "Custom dates": the range they had (all time: 30 days). */
function seedCustom(range: ReportRange, now: Date): { from: string; to: string } {
  const days = range.preset === 'custom' ? null : rangeDays(range, now);
  if (days) return { from: dayString(days.first), to: dayString(days.last) };
  const today = dayNumberOf(now);
  return { from: dayString(today - 30), to: dayString(today) };
}

// ---------------------------------------------------------------------------------------------
// The spec state
// ---------------------------------------------------------------------------------------------

/** A fresh spec for a subject: its default measure and grouping, the last 30 days, no filters, bars. */
export function emptySpec(subject: string = DEFAULT_SUBJECT, catalog?: ReportCatalog | null): ReportSpec {
  const subj = subjectOf(subject, catalog);
  const def = own(SUBJECT_DEFAULTS, subject) ?? { measure: '', rows: null };
  let measure = def.measure;
  let rows = def.rows;
  if (subj) {
    if (!subj.measures.some((m) => m.key === measure)) measure = subj.measures[0]?.key ?? '';
    if (rows && !subj.groupings.some((g) => g.key === rows)) rows = subj.groupings[0]?.key ?? null;
  }
  return {
    spec_version: SPEC_VERSION,
    subject,
    measure,
    rows,
    columns: null,
    time: null,
    range: { preset: DEFAULT_PRESET },
    filters: emptyFilters(),
    chart: 'bars',
  };
}

/**
 * The first rule the spec breaks, as the sentence to show, or null when it can run. The rules are
 * plan B4.3's plus the two crmReportBuilder/spec.js adds (plan B13 R-3: custom dates between 2000
 * and 2099; a lead source of at most 200 characters with no control character), in spec.js's order
 * and with its sentences; `now` only matters for the presets' spans.
 */
export function specProblem(spec: ReportSpec, catalog?: ReportCatalog | null, now: Date = new Date()): string | null {
  const subject = subjectOf(spec.subject, catalog);
  if (!subject) return 'Choose what to count.';
  const measure = measureOf(subject, spec.measure);
  if (!measure) return 'Choose a measure.';
  const subjectWord = subject.label.toLowerCase();
  if (spec.rows && !groupingOf(subject, spec.rows)) return `${subject.label} can't be grouped by "${spec.rows}".`;
  if (spec.columns && !groupingOf(subject, spec.columns)) return `${subject.label} can't be grouped by "${spec.columns}".`;
  if (spec.columns && !spec.rows) return 'Choose the first grouping before a second one.';
  if (spec.rows && spec.columns && spec.rows === spec.columns) {
    return 'The two groupings are the same. Choose a different second grouping.';
  }

  const grain = spec.time ? spec.time.grain : null;
  const grainWords = GRAIN_CHOICES.find((g) => g.key === grain) ?? null;
  if (spec.time && !grainWords) return 'Choose day, week or month.';

  const family = measure.family;
  if (family && spec.columns) {
    return `"${measure.label}" already splits by ${family.label.toLowerCase()}, which takes the second grouping.`;
  }
  if (spec.columns && spec.time) return "A second grouping can't be combined with a time split.";
  if (family && spec.rows && grainWords) {
    return `"${measure.label}" already uses the second grouping, so it can't also be split by ${grainWords.unit}.`;
  }
  if (axisCount(spec, catalog) > MAX_AXES) return 'Two ways at most: a time split counts as one.';

  const range = spec.range;
  if (!range || !RANGE_CHOICES.some((r) => r.key === range.preset)) return 'Choose a date range.';
  if (range.preset === 'custom') {
    const first = parseDay(range.from);
    const last = parseDay(range.to);
    if (first === null || last === null) return 'Enter a start date and an end date.';
    for (const day of [first, last]) {
      const year = yearOf(day);
      if (year < MIN_YEAR || year > MAX_YEAR) return `Choose dates between ${MIN_YEAR} and ${MAX_YEAR}.`;
    }
    if (first > last) return 'The start date is after the end date.';
    if (overMaxYears(first, last)) return `A date range can cover ${MAX_RANGE_YEARS} years at most.`;
  }
  if (grainWords && range.preset === 'all') {
    return "All time has no start date, so it can't be split by day, week or month. Choose a date range.";
  }
  if (grain && grainWords) {
    const days = rangeDays(range, now);
    if (days) {
      const n = bucketCount(days.first, days.last, grain);
      const max = GRAIN_MAX[grain];
      if (n > max) {
        const hint =
          grain === 'day' ? 'Choose a shorter range or split by week.'
            : grain === 'week' ? 'Choose a shorter range or split by month.'
              : 'Choose a shorter range.';
        return `This range covers ${n} ${grainWords.units}; split by ${grainWords.unit} it can show ${max} at most. ${hint}`;
      }
    }
  }

  const filters = spec.filters ?? emptyFilters();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    // Whitespace alone is no filter: toWireSpec sends it as null and spec.js skips it the same way.
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) continue;
    const def = subject.filters.find((f) => f.key === key);
    if (!def) return `${FILTER_WORDS[key]} can't filter ${subjectWord}.`;
    if ((key === 'study_id' || key === 'owner') && !UUID_RE.test(value)) {
      return `That ${def.label.toLowerCase()} isn't one the report can use. Choose it again.`;
    }
    if (def.options && !def.options.some((o) => o.key === value)) {
      return `That ${def.label.toLowerCase()} isn't one the report can use. Choose it again.`;
    }
    // A free-text filter (the lead source: utm values are kept as sent) is at most 200 characters
    // with no control character, spec.js filterValue's rule for a filter with no fixed list.
    if (key !== 'study_id' && key !== 'owner' && !def.options
      && (typeof value !== 'string' || value.length > MAX_LEAD_SOURCE_LENGTH || hasControlCharacter(value))) {
      return `That ${def.label.toLowerCase()} isn't one the report can use. Choose it again.`;
    }
  }

  if (!CHART_CHOICES.some((c) => c.key === spec.chart)) return 'Choose bars or a table.';
  return null;
}

/** One step of the builder's state machine. */
export type BuilderChange =
  | { kind: 'subject'; value: string }
  | { kind: 'measure'; value: string }
  | { kind: 'rows'; value: string | null }
  | { kind: 'columns'; value: string | null }
  | { kind: 'grain'; value: TimeGrain | null }
  | { kind: 'range'; preset: ReportRangePreset; from?: string | null; to?: string | null }
  | { kind: 'filter'; key: ReportFilterKey; value: string | null }
  | { kind: 'chart'; value: ReportChart };

export interface ApplyOptions {
  catalog?: ReportCatalog | null;
  now?: Date;
}

/**
 * Apply one change and return the next spec (a new object; the SAME object when nothing changed). It
 * applies what it is given literally, with three documented cascades:
 *   - a new subject starts from that subject's defaults (emptySpec), keeping the date range, the chart
 *     and every filter the new subject also has;
 *   - clearing the first grouping moves the second grouping up into its place;
 *   - "Custom dates" with no dates starts from the days the current range covers.
 * It never refuses and never drops a choice to make room: the panel offers only the choices
 * nextChoices leaves enabled, and specProblem holds Run back with its sentence for anything else.
 */
export function applyChoice(spec: ReportSpec, change: BuilderChange, opts: ApplyOptions = {}): ReportSpec {
  switch (change.kind) {
    case 'subject': {
      if (change.value === spec.subject) return spec;
      const next = emptySpec(change.value, opts.catalog);
      const subject = subjectOf(change.value, opts.catalog);
      const filters = emptyFilters();
      for (const key of FILTER_KEYS) {
        const value = spec.filters[key];
        if (value && subject?.filters.some((f) => f.key === key)) filters[key] = value;
      }
      return { ...next, range: { ...spec.range }, chart: spec.chart, filters };
    }
    case 'measure':
      return change.value === spec.measure ? spec : { ...spec, measure: change.value };
    case 'rows': {
      if (change.value === spec.rows) return spec;
      if (change.value === null) return { ...spec, rows: spec.columns, columns: null };
      return { ...spec, rows: change.value };
    }
    case 'columns':
      return change.value === spec.columns ? spec : { ...spec, columns: change.value };
    case 'grain': {
      const current = spec.time ? spec.time.grain : null;
      if (change.value === current) return spec;
      return { ...spec, time: change.value ? { grain: change.value } : null };
    }
    case 'range': {
      if (change.preset !== 'custom') {
        if (spec.range.preset === change.preset) return spec;
        return { ...spec, range: { preset: change.preset } };
      }
      const base =
        spec.range.preset === 'custom'
          ? { from: spec.range.from ?? null, to: spec.range.to ?? null }
          : seedCustom(spec.range, opts.now ?? new Date());
      const from = change.from !== undefined ? change.from : base.from;
      const to = change.to !== undefined ? change.to : base.to;
      if (spec.range.preset === 'custom' && (spec.range.from ?? null) === from && (spec.range.to ?? null) === to) return spec;
      return { ...spec, range: { preset: 'custom', from, to } };
    }
    case 'filter': {
      const value = change.value === '' ? null : change.value;
      if ((spec.filters[change.key] ?? null) === value) return spec;
      return { ...spec, filters: { ...spec.filters, [change.key]: value } };
    }
    case 'chart':
      return change.value === spec.chart ? spec : { ...spec, chart: change.value };
    default:
      return spec;
  }
}

/** One option in the spec panel. */
export interface BuilderChoice {
  /** The key it sets; null = "None". */
  value: string | null;
  label: string;
  selected: boolean;
  disabled: boolean;
  /** Why it is disabled (a plain sentence); null when it can be picked. */
  reason: string | null;
  /** The change that picks it: applyChoice(spec, choice.change, { catalog }). */
  change: BuilderChange;
}

export interface FilterChoice {
  key: ReportFilterKey;
  label: string;
  value: string | null;
  /** A fixed list (eligibility); null = the UI supplies the values (studies, the team, sources). */
  options: ReportOption[] | null;
}

export interface BuilderChoices {
  subject: BuilderChoice[];
  measure: BuilderChoice[];
  /** "None" first, then the subject's groupings. */
  rows: BuilderChoice[];
  columns: BuilderChoice[];
  /** "None" first, then day, week and month. */
  grain: BuilderChoice[];
  range: BuilderChoice[];
  chart: BuilderChoice[];
  filters: FilterChoice[];
  axes: { used: number; max: number };
  /** Why the current spec cannot run (specProblem), or null. */
  problem: string | null;
}

/**
 * Every choice the panel can offer for this spec, each with `disabled` and the reason. A choice is
 * disabled when picking it would break a rule the spec does not already break, so a person can always
 * pick the choice that FIXES a problem (a saved report that no longer validates, say).
 */
export function nextChoices(spec: ReportSpec, catalog: ReportCatalog | null | undefined, now: Date = new Date()): BuilderChoices {
  const opts: ApplyOptions = { catalog, now };
  const base = specProblem(spec, catalog, now);
  const choice = (value: string | null, label: string, change: BuilderChange, selected: boolean): BuilderChoice => {
    const next = applyChoice(spec, change, opts);
    const problem = next === spec ? base : specProblem(next, catalog, now);
    const disabled = problem !== null && problem !== base;
    return { value, label, selected, disabled, reason: disabled ? problem : null, change };
  };
  const subject = subjectOf(spec.subject, catalog);
  const groupings = subject ? subject.groupings : [];
  const grain = spec.time ? spec.time.grain : null;
  return {
    subject: resolveCatalog(catalog).subjects.map((s) =>
      choice(s.key, s.label, { kind: 'subject', value: s.key }, s.key === spec.subject)),
    measure: (subject ? subject.measures : []).map((m) =>
      choice(m.key, m.label, { kind: 'measure', value: m.key }, m.key === spec.measure)),
    rows: [
      choice(null, 'None', { kind: 'rows', value: null }, spec.rows === null),
      ...groupings.map((g) => choice(g.key, g.label, { kind: 'rows', value: g.key }, g.key === spec.rows)),
    ],
    columns: [
      choice(null, 'None', { kind: 'columns', value: null }, spec.columns === null),
      ...groupings.map((g) => choice(g.key, g.label, { kind: 'columns', value: g.key }, g.key === spec.columns)),
    ],
    grain: [
      choice(null, 'None', { kind: 'grain', value: null }, grain === null),
      ...GRAIN_CHOICES.map((g) => choice(g.key, g.label, { kind: 'grain', value: g.key }, g.key === grain)),
    ],
    range: RANGE_CHOICES.map((r) => choice(r.key, r.label, { kind: 'range', preset: r.key }, r.key === spec.range.preset)),
    chart: CHART_CHOICES.map((c) => choice(c.key, c.label, { kind: 'chart', value: c.key }, c.key === spec.chart)),
    filters: (subject ? subject.filters : []).map((f) => ({
      key: f.key,
      label: f.label,
      value: spec.filters[f.key] ?? null,
      options: f.options,
    })),
    axes: { used: axisCount(spec, catalog), max: MAX_AXES },
    problem: base,
  };
}

/** True when two specs would run the same report (what is sent, not how it was reached). */
export function sameSpec(a: ReportSpec | null | undefined, b: ReportSpec | null | undefined): boolean {
  if (!a || !b) return a === b;
  return JSON.stringify(toWireSpec(a)) === JSON.stringify(toWireSpec(b));
}

/**
 * The "how this counts" line shown under every result (plan A4.2, B4.3): the subject's rule, then
 * what the measure and the time split add.
 */
export function countsAsLine(spec: ReportSpec, catalog?: ReportCatalog | null): string {
  const subject = subjectOf(spec.subject, catalog);
  if (!subject) return '';
  const parts: string[] = [];
  if (subject.counts_as) parts.push(asSentence(subject.counts_as));
  const measure = measureOf(subject, spec.measure);
  if (measure) {
    const split = !!(spec.rows || spec.columns || spec.time);
    if (measure.family && !measure.additive) {
      parts.push(`A lead can count under more than one ${measure.family.label.toLowerCase()}.`);
    } else if (!measure.family && !measure.additive && split) {
      parts.push('Each lead counts once in every group it falls in, so the groups can add up to more than the total.');
    }
    if (unitOf(measure, measure.key) === 'seconds') parts.push(`${measure.label} is shown in minutes.`);
  }
  if (spec.time && spec.time.grain === 'week') parts.push('Weeks start on Monday.');
  return parts.join(' ');
}

// ---------------------------------------------------------------------------------------------
// The time axis
// ---------------------------------------------------------------------------------------------

export interface TimeLabel {
  /** The bucket's first day, 'YYYY-MM-DD' (the value of the rows' `bucket`). */
  key: string;
  /** Under a bar or at the head of a column: "Sep 21", or "Sep '26" for a month. */
  short: string;
  /** For a tooltip: "Mon, Sep 21, 2026", "Week of Sep 21, 2026", "September 2026". */
  long: string;
}

const MAX_TIME_LABELS = 1000;
const formatters = new Map<string, Intl.DateTimeFormat>();
const formatDay = (n: number, options: Intl.DateTimeFormatOptions): string => {
  const cacheKey = JSON.stringify(options);
  let f = formatters.get(cacheKey);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...options });
    formatters.set(cacheKey, f);
  }
  return f.format(new Date(n * DAY_MS));
};

function labelBucket(n: number, grain: TimeGrain): TimeLabel {
  const key = dayString(n);
  if (grain === 'month') {
    const yy = key.slice(2, 4);
    return { key, short: `${formatDay(n, { month: 'short' })} '${yy}`, long: formatDay(n, { month: 'long', year: 'numeric' }) };
  }
  const short = formatDay(n, { month: 'short', day: 'numeric' });
  if (grain === 'week') {
    return { key, short, long: `Week of ${formatDay(n, { month: 'short', day: 'numeric', year: 'numeric' })}` };
  }
  return { key, short, long: formatDay(n, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) };
}

/**
 * The time axis: every bucket from the start of the range to its end, including the empty ones (a
 * chart that skipped a quiet week would lie), plus any bucket the rows hold outside that span (never
 * dropped). Chronological. [] without a grain.
 */
export function timeLabels(
  result: Pick<ReportResult, 'rows' | 'spec'> | null | undefined,
  grain: TimeGrain | null | undefined,
  now: Date = new Date(),
): TimeLabel[] {
  if (!grain) return [];
  const seen = new Set<number>();
  const rowDays: number[] = [];
  for (const r of result?.rows ?? []) {
    const n = parseDay(typeof r.bucket === 'string' ? r.bucket.slice(0, 10) : null);
    if (n !== null) rowDays.push(n);
  }
  const days = result?.spec ? rangeDays(result.spec.range, now) : null;
  let lo: number | null = days ? bucketStart(days.first, grain) : null;
  let hi: number | null = days ? bucketStart(days.last, grain) : null;
  for (const n of rowDays) {
    const b = bucketStart(n, grain);
    if (lo === null || b < lo) lo = b;
    if (hi === null || b > hi) hi = b;
  }
  const out: number[] = [];
  if (lo !== null && hi !== null) {
    for (let n = lo; n <= hi && out.length < MAX_TIME_LABELS; n = nextBucket(n, grain)) {
      out.push(n);
      seen.add(n);
    }
  }
  for (const n of rowDays) {
    if (!seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  out.sort((x, y) => x - y);
  return out.map((n) => labelBucket(n, grain));
}

// ---------------------------------------------------------------------------------------------
// The pivot: a result into table rows, columns and bar widths
// ---------------------------------------------------------------------------------------------

/** group: sorted by size; status and eligibility: their fixed order; time: by date; measure: one value. */
export type PivotAxisKind = 'group' | 'status' | 'eligibility' | 'time' | 'measure';

export interface PivotAxis {
  kind: PivotAxisKind;
  /** The grouping or family key ('study', 'reached'), the grain for time, the measure key for 'measure'. */
  key: string;
  /** The header over this axis ("Study", "Stage", "Week"; the measure's name for 'measure'). */
  label: string;
}

export interface PivotHeader {
  key: string;
  label: string;
  /** Tooltip words: the long date for a time column, else the label. */
  long: string;
  isOther: boolean;
  isNone: boolean;
}

export interface PivotCell {
  /** Raw (seconds for talk time); 0 where no row came back. */
  value: number;
  /** Bar width, BarRow's rule against the table's largest cell; a zero is 0. */
  pct: number;
  /** True where no row came back for this cell. */
  empty: boolean;
}

export interface PivotRow extends PivotHeader {
  cells: PivotCell[];
  /** The row's total across its columns: the sum for an additive measure, else null (it would double count). */
  total: number | null;
}

export interface PivotTable {
  rowAxis: PivotAxis;
  columnAxis: PivotAxis;
  columns: PivotHeader[];
  /** Real groups first, then missing-value ("(none)") rows, then "Other (N more)"; the last two flagged. */
  rows: PivotRow[];
  /** Per column: the sum for an additive measure, else the server's total when it sent one, else null. */
  columnTotals: (number | null)[];
  /** The sum for an additive measure, else the server's total when it sent one, else null. */
  grandTotal: number | null;
  /** The largest cell (the bars' 100 percent). */
  max: number;
  unit: ReportUnit;
  additive: boolean;
  /** How many rows and columns are "Other" or "(none)". */
  flagged: { other: number; none: number };
  /** True when no row came back at all. */
  empty: boolean;
}

export interface PivotOptions {
  catalog?: ReportCatalog | null;
  /**
   * A display name for a key that came back without a label (a teammate's id, say); return null to
   * keep the server's words. Called with the grouping key and the value key.
   */
  labelFor?: (grouping: string, key: string) => string | null | undefined;
  /** For the time axis's span (presets are relative to now). */
  now?: Date;
}

/** BarRow's width rule (components/reports/BarRow.tsx:18), except that a zero is 0 rather than 2. */
export function barPct(value: number, max: number): number {
  if (!(max > 0) || !(value > 0)) return 0;
  return Math.min(100, Math.max(2, Math.round((value / max) * 100)));
}

interface AxisDef extends PivotAxis {
  /** status and eligibility: the fixed order and words. */
  vocabulary: readonly ReportOption[] | null;
  /** Family members to show even at zero. */
  members: readonly ReportOption[] | null;
  /** The grouping key handed to labelFor (null for family, time and measure axes). */
  grouping: string | null;
}

interface HeaderAcc {
  key: string;
  label: string | null;
  weight: number;
}

function groupingAxis(key: string, subject: ReportSubject | null): AxisDef {
  const label = groupingOf(subject, key)?.label ?? humanize(key);
  if (key === 'to_status') return { kind: 'status', key, label, vocabulary: STATUS_WORDS, members: null, grouping: key };
  if (key === 'eligibility') return { kind: 'eligibility', key, label, vocabulary: ELIGIBILITY_WORDS, members: null, grouping: key };
  return { kind: 'group', key, label, vocabulary: null, members: null, grouping: key };
}

function familyAxis(family: ReportFamily): AxisDef {
  const members = family.members && family.members.length ? family.members : null;
  return {
    kind: 'status',
    key: family.key,
    label: family.label,
    vocabulary: members ? [...members, ...STATUS_WORDS.filter((w) => !members.some((m) => m.key === w.key))] : STATUS_WORDS,
    members,
    grouping: null,
  };
}

function headerLabel(def: AxisDef, key: string, serverLabel: string | null, labelFor: PivotOptions['labelFor']): string {
  if (isOtherKey(key)) return serverLabel || 'Other';
  if (isNoneKey(key)) return serverLabel || '(none)';
  const own = serverLabel && serverLabel !== key ? serverLabel : null;
  if (def.vocabulary) {
    return own ?? def.vocabulary.find((w) => w.key === key)?.label ?? humanize(key);
  }
  if (own) return own;
  if (labelFor && def.grouping) {
    const named = labelFor(def.grouping, key);
    if (named) return named;
  }
  return serverLabel || key;
}

function orderHeaders(def: AxisDef, accs: HeaderAcc[], labelOf: (a: HeaderAcc) => string): HeaderAcc[] {
  const byLabel = (x: HeaderAcc, y: HeaderAcc) => labelOf(x).localeCompare(labelOf(y));
  const normal = accs.filter((a) => !isOtherKey(a.key) && !isNoneKey(a.key));
  const nones = accs.filter((a) => isNoneKey(a.key)).sort(byLabel);
  const others = accs.filter((a) => isOtherKey(a.key)).sort(byLabel);
  if (def.kind === 'time') {
    normal.sort((x, y) => (x.key < y.key ? -1 : x.key > y.key ? 1 : 0));
  } else if (def.vocabulary) {
    const vocabulary = def.vocabulary;
    const rank = (a: HeaderAcc): number => {
      const i = vocabulary.findIndex((w) => w.key === a.key);
      return i < 0 ? vocabulary.length : i;
    };
    normal.sort((x, y) => rank(x) - rank(y) || byLabel(x, y));
  } else {
    normal.sort((x, y) => y.weight - x.weight || byLabel(x, y));
  }
  return [...normal, ...nones, ...others];
}

/**
 * The result as a table. Rows are the first grouping (else the family's axis, else one row for the
 * measure); columns are the second axis (the second grouping or the family's axis), else the time
 * buckets, else one value column. Nothing is dropped: every row of the result lands in exactly one
 * cell (two rows for one cell are added together), an absent cell is an empty 0, the time axis has
 * no gaps, and the "(none)" and "Other (N more)" rows and columns come last, flagged.
 */
export function pivot(result: ReportResult, opts: PivotOptions = {}): PivotTable {
  const spec = result.spec;
  const subject = subjectOf(spec.subject, opts.catalog);
  const measure = measureOf(subject, spec.measure);
  const family = measure ? measure.family : null;
  const unit = unitOf(measure, spec.measure);
  const additive = measure ? measure.additive : false;
  const grain = spec.time ? spec.time.grain : null;
  const measureName = measure ? measure.label : humanize(spec.measure || 'value');
  const measureAxis: AxisDef = {
    kind: 'measure',
    key: spec.measure,
    label: unit === 'seconds' ? `${measureName} (minutes)` : measureName,
    vocabulary: null,
    members: null,
    grouping: null,
  };
  const timeAxis: AxisDef | null = grain
    ? { kind: 'time', key: grain, label: GRAIN_CHOICES.find((g) => g.key === grain)?.label ?? humanize(grain), vocabulary: null, members: null, grouping: null }
    : null;
  const aDef = spec.rows ? groupingAxis(spec.rows, subject) : null;
  const bDef = family ? familyAxis(family) : spec.columns ? groupingAxis(spec.columns, subject) : null;

  // A family with no first grouping may come back keyed on a_key; read it from there.
  const bFromA = !aDef && !!bDef && result.rows.length > 0
    && result.rows.every((r) => r.b_key === null) && result.rows.some((r) => r.a_key !== null);
  const bKey = (r: ReportResultRow) => (bFromA ? r.a_key : r.b_key);
  const bLabel = (r: ReportResultRow) => (bFromA ? r.a_label : r.b_label);

  const rowDef: AxisDef = aDef ?? bDef ?? measureAxis;
  const colDef: AxisDef = aDef && bDef ? bDef : timeAxis ?? measureAxis;

  const fieldOf = (def: AxisDef): ((r: ReportResultRow) => { key: string; label: string | null }) => {
    if (def === aDef) return (r) => ({ key: isNoneKey(r.a_key) ? r.a_key || NONE_KEYS[0] : (r.a_key as string), label: r.a_label });
    if (def === bDef) {
      return (r) => {
        const k = bKey(r);
        return { key: isNoneKey(k) ? k || NONE_KEYS[0] : (k as string), label: bLabel(r) };
      };
    }
    if (def === timeAxis) return (r) => ({ key: r.bucket ? r.bucket.slice(0, 10) : NONE_KEYS[0], label: null });
    return () => ({ key: MEASURE_KEY, label: null });
  };
  const rowField = fieldOf(rowDef);
  const colField = fieldOf(colDef);

  const rowAccs = new Map<string, HeaderAcc>();
  const colAccs = new Map<string, HeaderAcc>();
  const sums = new Map<string, number>();
  const cellId = (rk: string, ck: string) => `${rk}\u0000${ck}`;
  const touch = (map: Map<string, HeaderAcc>, key: string, label: string | null, weight: number) => {
    const acc = map.get(key);
    if (acc) {
      acc.weight += weight;
      if (!acc.label && label) acc.label = label;
    } else {
      map.set(key, { key, label: label || null, weight });
    }
  };
  for (const r of result.rows) {
    const rf = rowField(r);
    const cf = colField(r);
    const value = Number.isFinite(r.value) ? r.value : 0;
    touch(rowAccs, rf.key, rf.label, Math.abs(value));
    touch(colAccs, cf.key, cf.label, Math.abs(value));
    const id = cellId(rf.key, cf.key);
    sums.set(id, (sums.get(id) ?? 0) + value);
  }

  // Keep the shape: family members at zero, every time bucket, and the one measure row or column.
  const labels = timeAxis ? timeLabels(result, grain, opts.now) : [];
  const timeWords = new Map(labels.map((l) => [l.key, l] as [string, TimeLabel]));
  const fill = (def: AxisDef, map: Map<string, HeaderAcc>) => {
    if (def.members) def.members.forEach((m) => touch(map, m.key, null, 0));
    if (def === timeAxis) labels.forEach((l) => touch(map, l.key, null, 0));
    if (def === measureAxis) touch(map, MEASURE_KEY, null, 0);
  };
  fill(rowDef, rowAccs);
  fill(colDef, colAccs);

  const labelFn = (def: AxisDef) => (acc: HeaderAcc): string => {
    if (def === measureAxis) return def.label;
    if (def === timeAxis) return timeWords.get(acc.key)?.short ?? (isNoneKey(acc.key) ? '(none)' : acc.key);
    return headerLabel(def, acc.key, acc.label, opts.labelFor);
  };
  const header = (def: AxisDef, acc: HeaderAcc): PivotHeader => {
    const label = labelFn(def)(acc);
    return {
      key: acc.key,
      label,
      long: def === timeAxis ? timeWords.get(acc.key)?.long ?? label : label,
      isOther: isOtherKey(acc.key),
      isNone: def !== measureAxis && isNoneKey(acc.key),
    };
  };
  const rowOrder = orderHeaders(rowDef, Array.from(rowAccs.values()), labelFn(rowDef));
  const colOrder = orderHeaders(colDef, Array.from(colAccs.values()), labelFn(colDef));
  const columns = colOrder.map((acc) => header(colDef, acc));

  let max = 0;
  sums.forEach((v) => {
    if (v > max) max = v;
  });

  // The server's total call has no dimensions (plan B4.3), so a key it still carries is a placeholder
  // (funnel_stats says 'overall'): match a total on the column's own key, and only when exactly one
  // total row has it.
  const totalRows = result.totals ? result.totals.rows : [];
  const only = (hits: ReportResultRow[]): number | null => (hits.length === 1 ? hits[0].value : null);
  const serverGrand = result.totals && result.totals.value !== null ? result.totals.value : only(totalRows);
  const serverColumnTotal = (key: string): number | null => {
    if (colDef === measureAxis) return serverGrand;
    if (colDef === timeAxis) return only(totalRows.filter((r) => !!r.bucket && r.bucket.slice(0, 10) === key));
    return only(totalRows.filter((r) => r.bucket === null && bKey(r) === key));
  };

  const rows: PivotRow[] = rowOrder.map((acc) => {
    const cells: PivotCell[] = colOrder.map((col) => {
      const v = sums.get(cellId(acc.key, col.key));
      return v === undefined ? { value: 0, pct: 0, empty: true } : { value: v, pct: barPct(v, max), empty: false };
    });
    const sum = cells.reduce((s, c) => s + c.value, 0);
    return { ...header(rowDef, acc), cells, total: additive ? sum : null };
  });
  const allSum = rows.reduce((s, r) => s + r.cells.reduce((t, c) => t + c.value, 0), 0);
  const grandTotal = additive ? allSum : serverGrand;
  if (rowDef === measureAxis && rows.length === 1) rows[0].total = grandTotal;
  const columnTotals = colOrder.map((col, j) =>
    additive ? rows.reduce((s, r) => s + r.cells[j].value, 0) : serverColumnTotal(col.key));

  const flaggedOf = (hs: PivotHeader[], pick: (h: PivotHeader) => boolean) => hs.filter(pick).length;
  return {
    rowAxis: { kind: rowDef.kind, key: rowDef.key, label: rowDef === measureAxis ? '' : rowDef.label },
    columnAxis: { kind: colDef.kind, key: colDef.key, label: colDef.label },
    columns,
    rows,
    columnTotals,
    grandTotal,
    max,
    unit,
    additive,
    flagged: {
      other: flaggedOf(rows, (h) => h.isOther) + flaggedOf(columns, (h) => h.isOther),
      none: flaggedOf(rows, (h) => h.isNone) + flaggedOf(columns, (h) => h.isNone),
    },
    empty: result.rows.length === 0,
  };
}

export interface TimeBarSegment {
  key: string;
  label: string;
  value: number;
  /** Share of this bucket's bar, 0 to 100. */
  pct: number;
  isOther: boolean;
  isNone: boolean;
}

export interface TimeBar {
  key: string;
  label: string;
  long: string;
  /** The segments added up. */
  total: number;
  /** The bar's height against the tallest bucket, 0 to 100 (a nonzero bucket is at least 2). */
  heightPct: number;
  /** One per table row with a value in this bucket, in the table's row order (zeros left out). */
  segments: TimeBarSegment[];
}

/**
 * Bars over time, stacked by the table's rows (the ScreeningGateReport pattern): one bar per time
 * column of a pivot whose columns are time buckets; [] for any other table.
 */
export function timeBars(table: PivotTable): TimeBar[] {
  if (table.columnAxis.kind !== 'time') return [];
  const totals = table.columns.map((_, j) => table.rows.reduce((s, r) => s + r.cells[j].value, 0));
  const tallest = totals.reduce((m, t) => (t > m ? t : m), 0);
  return table.columns.map((col, j) => {
    const total = totals[j];
    const segments: TimeBarSegment[] = [];
    table.rows.forEach((r) => {
      const value = r.cells[j].value;
      if (value > 0) {
        segments.push({
          key: r.key,
          label: r.label,
          value,
          pct: total > 0 ? (value / total) * 100 : 0,
          isOther: r.isOther,
          isNone: r.isNone,
        });
      }
    });
    return { key: col.key, label: col.label, long: col.long, total, heightPct: barPct(total, tallest), segments };
  });
}

/** Talk time (seconds) as whole minutes; anything else unchanged. */
export function displayValue(value: number, unit: ReportUnit): number {
  return unit === 'seconds' ? Math.round(value / 60) : value;
}

/** A value for a cell: "1,234", or "21 min" for talk time; an em dash for no value. */
export function formatValue(value: number | null | undefined, unit: ReportUnit = 'count'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '\u2014';
  const shown = displayValue(value, unit).toLocaleString('en-US', { maximumFractionDigits: 1 });
  return unit === 'seconds' ? `${shown} min` : shown;
}

// ---------------------------------------------------------------------------------------------
// Saving, sharing, pinning, exporting
// ---------------------------------------------------------------------------------------------

/** The export's file name: the report's name as a slug, then the day, e.g. weekly-calls-2026-09-26.csv. */
export function csvFileName(name: string | null | undefined, now: Date = new Date()): string {
  const slug = (name ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 60)
    .replace(/-+$/, '');
  return `${slug || 'custom-report'}-${dayString(dayNumberOf(now))}.csv`;
}

/**
 * Whether to OFFER change, share and archive (display only; the server decides, plan B4.5): the author
 * always; an admin for a team report. Anyone who can see a report can open it and save a copy.
 */
export function canEdit(
  report: Pick<SavedReportSummary, 'owner_user_id' | 'visibility'> | null | undefined,
  who: { userId: string | null | undefined; isAdmin: boolean },
): boolean {
  if (!report) return false;
  if (who.userId && report.owner_user_id === who.userId) return true;
  return who.isAdmin && report.visibility === 'team';
}

/** The save dialog's check (crm.saved_reports: a name of 1 to 80 characters, a description of 500 at most). */
export function savedReportProblem(input: { name: string; description?: string | null }): string | null {
  const name = (input.name ?? '').trim();
  if (!name) return 'Give the report a name.';
  if (name.length > REPORT_NAME_MAX) return `A name can be ${REPORT_NAME_MAX} characters at most.`;
  const description = (input.description ?? '').trim();
  if (description.length > REPORT_DESCRIPTION_MAX) {
    return `A description can be ${REPORT_DESCRIPTION_MAX} characters at most.`;
  }
  return null;
}

/** The stored pins (a JSON array of report ids), junk-tolerant: bad JSON or bad entries read as none. */
export function parsePinned(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: string[] = [];
    for (const id of parsed) {
      if (typeof id === 'string' && UUID_RE.test(id) && !out.includes(id)) out.push(id);
    }
    return out.slice(-MAX_PINNED);
  } catch {
    return [];
  }
}

export const serializePinned = (ids: readonly string[]): string => JSON.stringify(parsePinned(JSON.stringify(ids)));

export const isPinned = (pinned: readonly string[], id: string): boolean => pinned.includes(id);

/** Pin (to the end of the pinned list) or unpin; the oldest pin falls off past MAX_PINNED. */
export function togglePinned(pinned: readonly string[], id: string): string[] {
  if (pinned.includes(id)) return pinned.filter((p) => p !== id);
  return [...pinned, id].slice(-MAX_PINNED);
}

/** Pinned reports first, in the order they were pinned; the rest after, in the order given. */
export function pinnedFirst<T extends { id: string }>(reports: readonly T[], pinned: readonly string[]): T[] {
  const top = pinned
    .map((id) => reports.find((r) => r.id === id))
    .filter((r): r is T => r !== undefined);
  return [...top, ...reports.filter((r) => !pinned.includes(r.id))];
}

// ---------------------------------------------------------------------------------------------
// The wire: what api.ts sends, and the tolerant readers for what comes back
// ---------------------------------------------------------------------------------------------

/**
 * Exactly the keys of plan B4.3's spec, nothing the UI keeps beside them: a custom range carries its
 * dates, a preset does not; an empty filter is null.
 */
export function toWireSpec(spec: ReportSpec): ReportSpec {
  const filters = emptyFilters();
  for (const key of FILTER_KEYS) {
    const value = spec.filters ? spec.filters[key] : null;
    filters[key] = typeof value === 'string' && value.trim() ? value : null;
  }
  const range: ReportRange =
    spec.range.preset === 'custom'
      ? { preset: 'custom', from: spec.range.from ?? null, to: spec.range.to ?? null }
      : { preset: spec.range.preset };
  return {
    spec_version: SPEC_VERSION,
    subject: spec.subject,
    measure: spec.measure,
    rows: spec.rows || null,
    columns: spec.columns || null,
    time: spec.time ? { grain: spec.time.grain } : null,
    range,
    filters,
    chart: spec.chart,
  };
}

const GRAIN_KEYS: readonly string[] = GRAIN_CHOICES.map((g) => g.key);
const PRESET_KEYS: readonly string[] = RANGE_CHOICES.map((r) => r.key);

function readRange(v: unknown): ReportRange {
  if (!isObj(v)) return { preset: DEFAULT_PRESET };
  const preset = text(v.preset);
  if (preset && PRESET_KEYS.includes(preset) && preset !== 'custom') return { preset: preset as RangePreset };
  if (preset === 'custom' || text(v.from) || text(v.to)) {
    return { preset: 'custom', from: text(v.from), to: text(v.to) };
  }
  return { preset: DEFAULT_PRESET };
}

/** A stored or echoed spec, with every field present; null when it has no subject at all. */
export function readSpec(raw: unknown): ReportSpec | null {
  if (!isObj(raw)) return null;
  const subject = text(raw.subject);
  if (!subject) return null;
  const grain = isObj(raw.time) ? text(raw.time.grain) : null;
  const f = isObj(raw.filters) ? raw.filters : {};
  return {
    spec_version: SPEC_VERSION,
    subject,
    measure: text(raw.measure) ?? '',
    rows: text(raw.rows),
    columns: text(raw.columns),
    time: grain && GRAIN_KEYS.includes(grain) ? { grain: grain as TimeGrain } : null,
    range: readRange(raw.range),
    filters: {
      study_id: text(f.study_id),
      owner: text(f.owner),
      lead_source: text(f.lead_source),
      eligibility: text(f.eligibility),
    },
    chart: raw.chart === 'table' ? 'table' : 'bars',
  };
}

function readOptions(v: unknown): ReportOption[] | null {
  if (!Array.isArray(v)) return null;
  const out: ReportOption[] = [];
  for (const o of v) {
    const key = typeof o === 'string' ? o : isObj(o) ? keyOf(o.key) ?? keyOf(o.value) : null;
    if (!key || out.some((x) => x.key === key)) continue;
    const label = isObj(o) ? text(o.label) : null;
    out.push({ key, label: label ?? STATUS_WORDS.find((w) => w.key === key)?.label ?? humanize(key) });
  }
  return out;
}

const V1_FAMILIES: ReadonlyMap<string, ReportFamily> = new Map(
  V1_CATALOG.subjects.flatMap((s) => s.measures)
    .filter((m) => m.family)
    .map((m) => [m.key, m.family as ReportFamily] as [string, ReportFamily]),
);

function readFamily(v: unknown, measureKey: string): ReportFamily | null {
  if (!v) return null;
  const known = V1_FAMILIES.get(measureKey) ?? null;
  if (v === true) {
    return { key: measureKey, label: known?.label ?? 'Stage', members: known?.members ?? null };
  }
  if (typeof v === 'string') {
    const k = V1_FAMILIES.get(v) ?? known;
    return { key: v, label: k?.label ?? humanize(v), members: k?.members ?? null };
  }
  if (isObj(v)) {
    const key = text(v.key) ?? measureKey;
    const k = V1_FAMILIES.get(key) ?? known;
    return {
      key,
      label: text(v.label) ?? k?.label ?? 'Stage',
      members: readOptions(v.members ?? v.values ?? v.stages) ?? k?.members ?? null,
    };
  }
  return null;
}

function readMeasure(v: unknown): ReportMeasure | null {
  const key = typeof v === 'string' ? v : isObj(v) ? text(v.key) : null;
  if (!key) return null;
  const o = isObj(v) ? v : {};
  return {
    key,
    label: text(o.label) ?? humanize(key),
    additive: o.additive === true,
    family: readFamily(o.family, key),
    admin: o.admin === true,
    unit: unitOf(null, key) === 'seconds' || text(o.unit) === 'seconds' ? 'seconds' : 'count',
  };
}

function readGrouping(v: unknown): ReportGrouping | null {
  const key = typeof v === 'string' ? v : isObj(v) ? text(v.key) : null;
  if (!key) return null;
  const o = isObj(v) ? v : {};
  return { key, label: text(o.label) ?? humanize(key), rule: text(o.rule) };
}

/** Catalog filter names (B4.3 calls them study, recruiter, teammate...) to the spec's filter keys. */
const FILTER_ALIASES: Readonly<Record<string, ReportFilterKey>> = {
  study_id: 'study_id',
  study: 'study_id',
  owner: 'owner',
  recruiter: 'owner',
  teammate: 'owner',
  recorded_by: 'owner',
  lead_source: 'lead_source',
  source: 'lead_source',
  eligibility: 'eligibility',
};

function readFilter(v: unknown): ReportFilterDef | null {
  const name = typeof v === 'string' ? v : isObj(v) ? text(v.key) : null;
  const key = name ? own(FILTER_ALIASES, name) : undefined;
  if (!key) return null;
  const o = isObj(v) ? v : {};
  return {
    key,
    label: text(o.label) ?? FILTER_WORDS[key],
    options: readOptions(o.options) ?? (key === 'eligibility' ? ELIGIBILITY_WORDS.map((w) => ({ ...w })) : null),
  };
}

const listOf = <T>(v: unknown, read: (x: unknown) => T | null, keyFn: (x: T) => string): T[] => {
  if (!Array.isArray(v)) return [];
  const out: T[] = [];
  for (const item of v) {
    const t = read(item);
    if (t && !out.some((x) => keyFn(x) === keyFn(t))) out.push(t);
  }
  return out;
};

function readSubject(v: unknown): ReportSubject | null {
  if (!isObj(v)) return null;
  const key = text(v.key);
  if (!key) return null;
  return {
    key,
    label: text(v.label) ?? humanize(key),
    mirrors: text(v.mirrors),
    time_label: text(v.time_label),
    counts_as: text(v.counts_as) ?? '',
    measures: listOf(v.measures, readMeasure, (m) => m.key),
    groupings: listOf(v.groupings, readGrouping, (g) => g.key),
    filters: listOf(v.filters, readFilter, (f) => f.key),
  };
}

/** GET /report-builder/catalog: `{ catalog: {...} }`, `{ subjects: [...] }` or the list itself. */
export function readCatalog(raw: unknown): ReportCatalog {
  const root = isObj(raw) && isObj(raw.catalog) ? raw.catalog : raw;
  const list = Array.isArray(root) ? root : isObj(root) ? root.subjects : null;
  return {
    spec_version: (isObj(root) ? numberOf(root.spec_version) : null) ?? SPEC_VERSION,
    subjects: listOf(list, readSubject, (s) => s.key),
  };
}

function readRow(v: unknown): ReportResultRow | null {
  if (!isObj(v)) return null;
  return {
    bucket: typeof v.bucket === 'string' && v.bucket ? v.bucket.slice(0, 10) : null,
    a_key: keyOf(v.a_key),
    a_label: text(v.a_label),
    b_key: keyOf(v.b_key),
    b_label: text(v.b_label),
    value: numberOf(v.value) ?? 0,
  };
}

const readRows = (v: unknown): ReportResultRow[] =>
  (Array.isArray(v) ? v : []).map(readRow).filter((r): r is ReportResultRow => r !== null);

function readTotals(v: unknown): ReportTotals {
  const n = numberOf(v);
  if (n !== null) return { value: n, rows: [] };
  if (Array.isArray(v)) return { value: null, rows: readRows(v) };
  if (isObj(v)) {
    return { value: numberOf(v.value) ?? numberOf(v.total) ?? numberOf(v.all), rows: readRows(v.rows) };
  }
  return { value: null, rows: [] };
}

const axisKeyOf = (v: unknown): string | null => (typeof v === 'string' ? text(v) : isObj(v) ? text(v.key) : null);

function readAxes(v: unknown, spec: ReportSpec, family: string | null): ReportAxes {
  const o = isObj(v) ? v : {};
  const grain = axisKeyOf(o.time) ?? axisKeyOf(o.grain);
  return {
    a: axisKeyOf(o.a) ?? axisKeyOf(o.dimA) ?? axisKeyOf(o.dim_a) ?? spec.rows,
    b: axisKeyOf(o.b) ?? axisKeyOf(o.dimB) ?? axisKeyOf(o.dim_b) ?? spec.columns ?? family,
    time: grain && GRAIN_KEYS.includes(grain) ? (grain as TimeGrain) : spec.time ? spec.time.grain : null,
  };
}

const countOf = (v: unknown): number | null => {
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (Array.isArray(v)) return v.length;
  return numberOf(v);
};

function readNotes(v: unknown, rows: ReportResultRow[]): ReportNotes {
  const o = isObj(v) ? v : {};
  const flagged = (pick: (k: string | null) => boolean) =>
    rows.filter((r) => pick(r.a_key) || pick(r.b_key)).length;
  return {
    counts_as: text(o.counts_as) ?? '',
    other_rows: countOf(o.other_rows) ?? flagged((k) => isOtherKey(k)),
    none_rows: countOf(o.none_rows) ?? flagged((k) => k !== null && isNoneKey(k)),
  };
}

/**
 * POST /report-builder/run: `{ axes, rows, totals, notes: { counts_as, other_rows, none_rows }, spec }`
 * (plan B4.5), read tolerantly. `sent` is the spec the browser sent, used when the answer echoes none.
 */
export function readResult(raw: unknown, sent: ReportSpec): ReportResult {
  const root = isObj(raw) && isObj(raw.result) ? raw.result : raw;
  const o = isObj(root) ? root : {};
  const rows = readRows(o.rows);
  const spec = readSpec(o.spec) ?? sent;
  const family = familyOf(spec);
  return {
    axes: readAxes(o.axes, spec, family ? family.key : null),
    rows,
    totals: readTotals(o.totals),
    notes: readNotes(o.notes, rows),
    spec,
  };
}

/** A row of GET /saved-reports; null without an id. */
export function readSavedSummary(raw: unknown): SavedReportSummary | null {
  if (!isObj(raw)) return null;
  const id = text(raw.id);
  if (!id) return null;
  const visibility: ReportVisibility = raw.visibility === 'team' ? 'team' : 'private';
  const specSubject = isObj(raw.spec) ? text(raw.spec.subject) : null;
  return {
    id,
    name: text(raw.name) ?? 'Untitled report',
    description: text(raw.description),
    visibility,
    owner_user_id: text(raw.owner_user_id),
    owner_name: text(raw.owner_name),
    subject: text(raw.subject) ?? specSubject,
    created_at: text(raw.created_at),
    updated_at: text(raw.updated_at),
  };
}

/** GET /saved-reports/:id and every write's `{ report }`; null without an id. */
export function readSavedReport(raw: unknown): SavedReport | null {
  const summary = readSavedSummary(raw);
  if (!summary || !isObj(raw)) return null;
  const spec = readSpec(raw.spec);
  return {
    ...summary,
    subject: summary.subject ?? (spec ? spec.subject : null),
    spec,
    spec_version: numberOf(raw.spec_version) ?? SPEC_VERSION,
    invalid: text(raw.invalid),
    archived_at: text(raw.archived_at),
  };
}
